import { useState, useRef, useEffect } from "react";
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Send,
  Bot,
  User,
  Mic,
  Camera,
  FileUp,
  Image,
  FileText,
  Check,
  UserRound,
  MessageSquare,
} from "lucide-react";
import humanimg from "../../humanimg.png";
function ChatInterface(props) {
  const [inputValue, setInputValue] = useState("");
  const { uploadedFiles = [], setUploadedFiles } = props;
  const [isUploading, setIsUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "bot",
      message: `Hi ${props?.claim?.customer?.fullName}! We see that Protection Plan ID ${props?.claim?.plan?.businessPlanID}—purchased about three months ago—covers your Samsung dishwasher, and you’ve reported that it isn’t draining properly. Did anything unusual happen during installation, or has anything unusual happened recently that might have triggered this issue?`,
      createdAt: new Date(),
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const newMessage = {
      id: Date.now(),
      sender: "user",
      message: inputValue.trim(),
      createdAt: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputValue("");
    setIsSending(true);
    setIsTyping(true);

    try {
      const res = await fetch("https://n8n.techfeatured.com/webhook/ai-bot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: inputValue.trim() }),
      });

      const data = await res.json();

      const botReply = {
        id: Date.now() + 1,
        sender: "bot",
        message: data.answer || "Sorry, something went wrong.",
        createdAt: new Date(),
      };

      setMessages((prev) => [...prev, botReply]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 2,
          sender: "bot",
          message: "Oops! Couldn't reach the server.",
          createdAt: new Date(),
        },
      ]);
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  };
  const sendMessage = (content, type = "text") => {
    const userMessage = {
      id: Date.now(),
      sender: "user",
      message: content,
      type,
      createdAt: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsSending(true);

    // Simulate bot reply
    setTimeout(() => {
      const botReply = {
        id: Date.now() + 1,
        sender: "bot",
        message:
          type === "file_upload"
            ? "Thanks for uploading the file(s). We'll review them shortly."
            : type === "audio_upload"
            ? "Thanks for your voice message. We'll process it soon."
            : type === "quick_reply"
            ? `Got it. "${content}" selected. Please describe the issue.`
            : "Thanks for your message. How else can I assist?",
        type: "text",
        createdAt: new Date(),
      };

      setMessages((prev) => [...prev, botReply]);
      setIsSending(false);
    }, 1500);
  };

  const handleFileUpload = (event, fileType) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files).map((file, index) => {
      const isPreviewable =
        file.type.startsWith("image/") || file.type.startsWith("video/");
      return {
        id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`,
        name: file.name,
        type: file.type,
        size: file.size,
        previewUrl: isPreviewable ? URL.createObjectURL(file) : null,
        file,
        uploadDate: new Date().toISOString(),
        status: "uploaded",
        category: fileType,
      };
    });

    // Append to uploaded files state
    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Trigger parent callback if needed
    if (props.onFilesUpload) {
      props.onFilesUpload(newFiles);
    }

    event.target.value = "";
  };

  const handleImageUpload = () => {
    imageInputRef.current?.click();
    // setShowUploadOptions(false);
  };

  const handleDocumentUpload = () => {
    documentInputRef.current?.click();
    // setShowUploadOptions(false);
  };

  const handleAudioRecord = () => {
    setIsRecording(true);
    // setShowUploadOptions(false);

    setTimeout(() => {
      const audioFile = {
        id: `audio-${Date.now()}`,
        name: `voice-memo-${new Date().toLocaleTimeString()}.mp3`,
        type: "audio/mp3",
        size: 1024 * 1024 * 2,
        previewUrl: null, // No preview for audio files
        file: new Blob([], { type: "audio/mp3" }), // Create a dummy blob
        uploadDate: new Date().toISOString(),
        status: "uploaded",
        category: "audio",
      };

      setUploadedFiles((prev) => [...prev, audioFile]);

      if (props.onFilesUpload) {
        props.onFilesUpload([audioFile]);
      }

      setIsRecording(false);
    }, 3000);
  };
  const formatTime = (date) =>
    new Date(date).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  useEffect(() => {
    const withPreview = uploadedFiles.map((file) => {
      if (
        (file.type?.startsWith("image/") || file.type?.startsWith("video/")) &&
        !file.previewUrl &&
        file.file instanceof File
      ) {
        return {
          ...file,
          previewUrl: URL.createObjectURL(file.file),
        };
      }
      return file;
    });

    setUploadedFiles(withPreview);
  }, [uploadedFiles]);

  return (
    <Card className="shadow-lg overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-xaxathrive-primary to-blue-600 text-white !p-4">
        <div className="flex items-center gap-3">
          <Avatar className="bg-white bg-opacity-20">
            <AvatarFallback className="bg-slate-100 text-slate-600">
              <img src={humanimg} className="w-10" />
            </AvatarFallback>
          </Avatar>
          <div>
            <h2 className="font-semibold text-slate-600">
              AI Claims Assistant - Ticket Id: CL-638881497142762090
            </h2>
            <p className="text-sm text-blue-100">
              Here to help you file your claim quickly
            </p>
          </div>
          <div className="ml-auto">
            <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
              <div className="w-2 h-2 bg-white rounded-full"></div>
              Online
            </span>
          </div>
        </div>
      </CardHeader>

      <ScrollArea className="h-80 p-4">
        <div className="space-y-3">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-2 ${
                msg.sender === "user" ? "justify-end" : ""
              }`}
            >
              {msg.sender !== "user" && (
                <Avatar className="bg-xaxathrive-primary">
                  <AvatarFallback className="bg-xaxathrive-primary text-white">
                    <img src={humanimg} className="w-10" />
                  </AvatarFallback>
                </Avatar>
              )}
              <div
                className={`flex-1 ${
                  msg.sender === "user" ? "text-right" : ""
                }`}
              >
                <div
                  className={`inline-block rounded-2xl px-4 py-3 max-w-lg ${
                    msg.sender === "user"
                      ? "bg-xaxathrive-primary text-white rounded-tr-sm ml-auto"
                      : "bg-slate-100 text-slate-800 rounded-tl-sm"
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {msg.sender === "user" ? "You" : "Emma"} •{" "}
                  {formatTime(msg.createdAt)}
                </p>
              </div>

              {msg.sender === "user" && (
                <Avatar className="bg-slate-300">
                  <AvatarFallback className="bg-slate-300 text-slate-600">
                    <User size={16} />
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          ))}
          <div ref={scrollRef} />
          {isTyping && (
            <div className="flex gap-2">
              <Avatar className="bg-xaxathrive-primary">
                <AvatarFallback className="bg-xaxathrive-primary text-white">
                  <img src={humanimg} className="w-10" />
                </AvatarFallback>
              </Avatar>
              <div className="bg-slate-100 text-slate-800 text-sm px-4 py-2 rounded-2xl max-w-fit">
                typing...
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <CardContent className="!p-6 border-t border-slate-200">
        <input
          ref={imageInputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*"
          onChange={(e) => handleFileUpload(e, "image")}
        />
        <input
          ref={documentInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx"
          onChange={(e) => handleFileUpload(e, "document")}
        />

        <div className="flex gap-3">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Add evidence"
            className="hover:bg-slate-100"
            onClick={() => setShowUploadOptions((prev) => !prev)}
          >
            +
          </Button>
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Type your message or describe the issue..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <Button
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 h-auto bg-xaxathrive-primary hover:bg-blue-700"
              onClick={handleSend}
              disabled={!inputValue.trim()}
            >
              <Send size={14} />
            </Button>
          </div>
        </div>
      </CardContent>
      {uploadedFiles.length > 0 && (
        <CardContent className="px-6 !py-3 border-t border-b border-slate-200 bg-slate-50 mb-2">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">
              Uploaded Files:
            </p>
            <div className="flex flex-wrap gap-2">
              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 bg-white border-slate-200 rounded-lg"
                >
                  {file.type.startsWith("image/") ? (
                    <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded">
                      <img
                        src={file.previewUrl}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    </div>
                  ) : file.type === "application/pdf" ? (
                    <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded">
                      <span className="text-red-600 font-semibold text-xs">
                        PDF
                      </span>
                    </div>
                  ) : file.type.startsWith("video/") ||
                    file.type.startsWith("audio/") ? (
                    <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded">
                      <Mic className="text-blue-600" size={24} />
                    </div>
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded">
                      <span className="text-gray-600 font-semibold text-xs">
                        DOC
                      </span>
                    </div>
                  )}

                  {/* <div className="flex flex-col overflow-hidden">
                    <span className="text-sm text-slate-700 truncate max-w-[8rem]">
                      {file.name}
                    </span>
                    <Check className="text-green-500" size={14} />
                  </div> */}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
      <CardContent>
        {showUploadOptions && (
          <div className="mb-4 !p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-slate-700">Add Evidence</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1"
                onClick={() => setShowUploadOptions(false)}
              >
                ×
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* <Button
                variant="outline"
                size="sm"
                className="!h-20 flex-col gap-2 hover:bg-red-50 hover:border-red-200"
                onClick={handleAudioRecord}
              >
                <Mic
                  size={24}
                  className={
                    isRecording ? "animate-pulse text-red-500" : "text-red-500"
                  }
                />
                <span className="text-xs font-medium">
                  {isRecording ? "Recording..." : "Record Audio"}
                </span>
              </Button> */}

              <Button
                variant="outline"
                size="sm"
                className="!h-20 flex-col gap-2 hover:bg-blue-50 hover:border-blue-200"
                onClick={handleImageUpload}
              >
                <Camera size={24} className="text-blue-500" />
                <span className="text-xs font-medium">Take Photo/Video</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="!h-20 flex-col gap-2 hover:bg-green-50 hover:border-green-200"
                onClick={handleDocumentUpload}
              >
                <FileUp size={24} className="text-green-500" />
                <span className="text-xs font-medium">Upload Doc</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ChatInterface;
